import { useCallback, useEffect, useRef, useState } from 'react'

import { useLazyGetProductReviewByIdQuery } from '../../api/product-reviews-api'
import { getReviewWithOrderedPhotos, updateReviewVoteState } from '../../model/review-state'
import { ProductReview, ProductReviewVote, ReviewMediaItem } from '../../model/types'

export const useMediaGalleryReviews = ({
	activeIndex,
	canVoteOptimistically,
	items,
	loadDetailedReviews,
	onVote,
	open
}: {
	activeIndex: number
	canVoteOptimistically: boolean
	items: ReviewMediaItem[]
	loadDetailedReviews: boolean
	onVote: (reviewId: string, vote: ProductReviewVote) => Promise<ProductReview | undefined> | undefined
	open: boolean
}) => {
	const [loadedReviews, setLoadedReviews] = useState<Record<string, ProductReview>>({})
	const requestedReviewIdsRef = useRef(new Set<string>())
	const localVoteRequestIdsRef = useRef<Record<string, number>>({})
	const [getProductReviewById] = useLazyGetProductReviewByIdQuery()

	useEffect(() => {
		if (!open || !loadDetailedReviews) return

		const reviewItems = [activeIndex - 1, activeIndex, activeIndex + 1]
			.map(index => items[index])
			.filter((item): item is ReviewMediaItem => Boolean(item?.reviewId && !item.review.comment))

		reviewItems.forEach(item => {
			const reviewId = item.reviewId
			if (!reviewId || loadedReviews[reviewId] || requestedReviewIdsRef.current.has(reviewId)) return

			requestedReviewIdsRef.current.add(reviewId)
			void getProductReviewById(reviewId)
				.unwrap()
				.then(review => {
					setLoadedReviews(current => ({ ...current, [reviewId]: review }))
				})
				.catch(() => {
					requestedReviewIdsRef.current.delete(reviewId)
				})
		})
	}, [activeIndex, getProductReviewById, items, loadDetailedReviews, loadedReviews, open])

	useEffect(() => {
		if (open) return

		setLoadedReviews({})
		requestedReviewIdsRef.current.clear()
	}, [open])

	const getItemWithLoadedReview = useCallback(
		(item: ReviewMediaItem): ReviewMediaItem => {
			const reviewId = item.reviewId
			const review = reviewId && loadedReviews[reviewId] ? loadedReviews[reviewId] : item.review
			return { ...item, review: getReviewWithOrderedPhotos(review, items) }
		},
		[items, loadedReviews]
	)

	const voteForLoadedReview = useCallback(
		async (reviewId: string, vote: ProductReviewVote) => {
			const requestId = (localVoteRequestIdsRef.current[reviewId] ?? 0) + 1
			localVoteRequestIdsRef.current[reviewId] = requestId

			const isLatestRequest = () => localVoteRequestIdsRef.current[reviewId] === requestId
			let previousReview: ProductReview | undefined

			if (!canVoteOptimistically) {
				await onVote(reviewId, vote)
				if (isLatestRequest()) delete localVoteRequestIdsRef.current[reviewId]
				return
			}

			setLoadedReviews(current => {
				previousReview = current[reviewId]
				const review =
					current[reviewId] ?? items.find(item => item.review.id === reviewId || item.reviewId === reviewId)?.review
				if (!review) return current

				return {
					...current,
					[reviewId]: updateReviewVoteState(review, vote)
				}
			})

			const updatedReview = await onVote(reviewId, vote)
			if (!isLatestRequest()) return

			if (!updatedReview) {
				if (previousReview) {
					setLoadedReviews(current => ({ ...current, [reviewId]: previousReview }))
				}

				delete localVoteRequestIdsRef.current[reviewId]
				return
			}

			setLoadedReviews(current => ({
				...current,
				[reviewId]: updateReviewVoteState(current[reviewId] ?? updatedReview, vote, updatedReview)
			}))
			delete localVoteRequestIdsRef.current[reviewId]
		},
		[canVoteOptimistically, items, onVote]
	)

	return {
		getItemWithLoadedReview,
		isReviewLoading: (item: ReviewMediaItem) =>
			Boolean(item.reviewId && !loadedReviews[item.reviewId] && !item.review.comment),
		voteForLoadedReview
	}
}
